import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import { exportStaffQuarterPriorityList, exportStaffQuarterOccupancyReport } from "../../../../core/data/redux/staffQuarterSlice";
import { toast } from "react-toastify";

const Reports = () => {
    const routes = all_routes;
    const dispatch = useDispatch<any>();
    const [isExporting, setIsExporting] = useState<string | null>(null);

    const handleGenerate = (title: string) => {
        setIsExporting(title);
        let action: any;
        let filename: string;
        
        if (title === "Priority List") {
            action = exportStaffQuarterPriorityList({});
            filename = `PriorityList_${new Date().getTime()}.xlsx`;
        } else if (title === "Occupancy Report") {
            action = exportStaffQuarterOccupancyReport({});
            filename = `OccupancyReport_${new Date().getTime()}.xlsx`;
        } else {
            toast.info("This report module is currently being finalized.");
            setIsExporting(null);
            return;
        }

        dispatch(action).then((res: any) => {
            if (res.payload) {
                const isCsv = title === "Priority List";
                const mimeType = isCsv ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                const extension = isCsv ? '.csv' : '.xlsx';
                
                const blob = res.payload instanceof Blob 
                    ? res.payload 
                    : new Blob([res.payload], { type: mimeType });
                
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename.replace('.xlsx', extension));
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success(`${title} exported successfully!`);
            } else {
                toast.error(`Failed to export ${title}. Check backend availability.`);
            }
            setIsExporting(null);
        }).catch(() => {
            toast.error("An error occurred during export connection.");
            setIsExporting(null);
        });
    };

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {/* Page Header */}
                <div className="page-header mb-4">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title text-dark fw-bold">SQ Management Reports</h3>
                            <ul className="breadcrumb bg-transparent p-0 mt-1">
                                <li className="breadcrumb-item">
                                    <Link to={routes.staffQuarterAdmin.dashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active">Reports</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                    {[
                        { title: "Priority List", desc: "View Priority list of the employees based on their eligibility.", icon: "ti-list-numbers", color: "text-primary" },
                        { title: "Occupancy Report", desc: "Detailed breakdown of occupied vs vacant houses by location.", icon: "ti-chart-pie", color: "text-success" },
                        { title: "Vacation Backlog", desc: "Houses where vacation is initiated but clearance is pending.", icon: "ti-alert-circle", color: "text-warning" },
                        { title: "Maintenance History", desc: "Audit trail of civil and E&M works on all quarters.", icon: "ti-history", color: "text-info" },
                        { title: "Allotment History", desc: "Log of all houses allotted in the current financial year.", icon: "ti-file-description", color: "text-secondary" },
                    ].map((report: any, idx) => (
                        <div className="col" key={idx}>
                            <div className="card h-100 border-0 shadow-sm hover-card" style={{ borderRadius: '16px' }}>
                                <div className="card-body p-4">
                                    <div className={`mb-3 ${report.color}`}>
                                        <i className={`ti ${report.icon}`} style={{ fontSize: '32px' }}></i>
                                    </div>
                                    <h5 className="fw-bold mb-2">{report.title}</h5>
                                    <p className="text-muted small mb-3">{report.desc}</p>
                                    {report.isScreen ? (
                                        <Link
                                            to={report.link}
                                            className="btn btn-primary w-100 rounded-pill d-flex align-items-center justify-content-center"
                                        >
                                            View Report
                                        </Link>
                                    ) : (
                                        <button 
                                            className="btn btn-primary w-100 rounded-pill d-flex align-items-center justify-content-center"
                                            onClick={() => handleGenerate(report.title)}
                                            disabled={isExporting !== null}
                                        >
                                            {isExporting === report.title ? (
                                                <>
                                                    <i className="ti ti-loader-2 spin me-2"></i> Generating...
                                                </>
                                            ) : (
                                                "Generate Report"
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <style>{`
                    .hover-card { transition: all 0.3s ease; }
                    .hover-card:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important; }
                    .spin { animation: spin 1s linear infinite; }
                    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                `}</style>
            </div>
        </div>
    );
};

export default Reports;
